import { memo, useMemo } from "react";
import { isHex, hexToRgba, rgbToRgba } from "@/lib/utils";
import { useAtomValue } from "jotai";
import { backgroundColorState, darkerBackgroundColorState, lighterBackgroundColorState, negativeState, positiveState, themeState } from "@/lib/store";

export const DataGridStyles = memo(() => {
    const theme = useAtomValue(themeState);
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const lighterBackgroundColor = useAtomValue(lighterBackgroundColorState);
    const darkerBackgroundColor = useAtomValue(darkerBackgroundColorState);

    const css = `
                    .MuiDataGrid-row .StackedBar_positive {
                        background-color: ${isHex(positive) ? hexToRgba(positive, 0.6) : rgbToRgba(positive, 0.6)};
                    }
                    .MuiDataGrid-row:hover .StackedBar_positive {
                        background-color: ${positive};
                    }
                    .MuiDataGrid-row .StackedBar_negative {
                        background-color: ${isHex(negative) ? hexToRgba(negative, 0.6) : rgbToRgba(negative, 0.6)};
                    }
                    .MuiDataGrid-row:hover .StackedBar_negative {
                        background-color: ${negative};
                    }

                    .MuiDataGrid-root .MuiDataGrid-columnHeader:focus,
                    .MuiDataGrid-root .MuiDataGrid-cell:focus {
                        outline: none !important;
                    }

                    .MuiDataGrid-pinnedColumns {
                        background-color: ${backgroundColor};
                    }
                    .MuiDataGrid-pinnedRows {
                        background-color: ${theme === "dark" ? lighterBackgroundColor : darkerBackgroundColor};
                    }
                    .MuiDataGrid-columnHeader {
                        min-height: unset !important;
                        padding: 4px 10px !important;
                    }
                    .MuiDataGrid-pinnedColumnHeaders {
                        height: auto !important;
                        min-height: unset !important;
                        top: unset !important;
                    }
                    .MuiDataGrid-pinnedColumns .MuiDataGrid-columnHeader {
                        height: auto !important;
                        min-height: unset !important;
                    }
                `;
    return <style>{css}</style>;
});

DataGridStyles.displayName = "DataGridStyles";

export const cellHoverStyleOptions = {
    "& .MuiDataGrid-cell:hover": {
        cursor: "pointer",
    },
};

export const tableStyleOptions = {
    border: 0,
    width: "100%",
    zIndex: 1,

    "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: "600",
        whiteSpace: "normal",
        lineHeight: "normal",
        maxHeight: "53px !important",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnHeaderTitle": {
        whiteSpace: "nowrap",
    },
    // Clip, not hidden: hidden makes this row a scroll container, and the pinned headers stick to it and
    // scroll away with the other columns while the pinned cells below stay put.
    "& .MuiDataGrid-columnHeaders": {
        overflow: "clip",
    },
    // On the rows and headers, not the scroller: a detail panel keeps its own sideways scrolling (useTouchAxisLock).
    "&[data-axis-lock] .MuiDataGrid-row, &[data-axis-lock] .MuiDataGrid-columnHeaders": {
        touchAction: "pan-y",
    },
    // MUI X 7+ always shows the column separators; MUI X 6 showed them on touch screens, and with a
    // mouse only on the hovered header.
    "@media (hover: hover)": {
        "& .MuiDataGrid-columnSeparator": {
            visibility: "hidden",
        },
        "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator--resizable": {
            visibility: "visible",
        },
    },
    "& .MuiDataGrid-sortIcon": {
        display: "flex",
        visibility: "visible",
        color: "grey",

        "@media screen and (min-width: 1920px)": {
            display: "flex",
            visibility: "visible",
        },
    },
    "& .MuiDataGrid-cell": {
        // MUI X 7+ cells are blocks; MUI X 6 laid them out as a centered flex row.
        display: "flex",
        alignItems: "center",
        fontSize: "11px",

        "@media screen and (min-width: 768px)": {
            fontSize: "12px",
        },
        "@media screen and (min-width: 1280px)": {
            fontSize: "12px",
        },
        "@media screen and (min-width: 1920px)": {
            fontSize: "14px",
        },
        "@media screen and (min-width: 3840px)": {
            fontSize: "18px",
        },

        wordWrap: "break-word !important",
    },
};

export const getCellStyleOptions = (theme: string) => ({
    "& .MuiDataGrid-columnHeaders, .MuiDataGrid-cell": {
        borderBottom: `1px solid ${theme === "dark" ? "rgb(81, 81, 81)" : "rgb(224, 224, 224)"}`,
        paddingBottom: "2px",
        paddingTop: "2px",
    },
});

export const useCellStyleOptions = () => {
    const theme = useAtomValue(themeState);
    return useMemo(() => getCellStyleOptions(theme), [theme]);
};
